import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { MerchantView, OrderStatus, OrderSummary, Page } from '@/api/types';
import { Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { StatusPill } from '@/components/StatusPill';
import { money } from '@/lib/format';

const STATUSES: OrderStatus[] = [
  'SUCCEEDED', 'FAILED', 'UNKNOWN', 'REVERSED', 'RESERVED',
  'NEEDS_MANUAL_REVIEW', 'SUBMITTED', 'CREATED', 'RESERVATION_CANCELLED',
];

export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  const status = params.get('status') ?? '';
  const agentReference = params.get('agentReference') ?? '';
  const target = params.get('target') ?? '';
  const merchantId = params.get('merchantId') ?? '';
  const minAmount = params.get('minAmount') ?? '';
  const maxAmount = params.get('maxAmount') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const query = new URLSearchParams({ page: String(page), size: '50' });
  for (const [key, value] of Object.entries({
    status, agentReference, target, merchantId, minAmount, maxAmount, from, to,
  })) {
    if (value) query.set(key, value);
  }

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['orders', query.toString()],
    queryFn: () => api.get<Page<OrderSummary>>(`/api/admin/orders?${query}`),
    placeholderData: keepPreviousData,
  });

  const { data: merchantsData } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });
  const merchants = [...(merchantsData?.merchants ?? [])].sort((a, b) => a.code.localeCompare(b.code));

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
    setPage(0);
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const csvQuery = new URLSearchParams();
      for (const [key, value] of Object.entries({
        status, agentReference, target, merchantId, minAmount, maxAmount, from, to,
      })) {
        if (value) csvQuery.set(key, value);
      }
      // Every matching order, not just the page on screen — streamed server-side so the export
      // isn't capped at whatever page size the table happens to be showing.
      const csv = await api.getRaw(`/api/admin/orders/csv?${csvQuery}`);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card
        title="Filters"
        actions={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void exportCsv()}
            disabled={!data?.content.length || exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        }
      >
        <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4 lg:grid-cols-6">
          <label>
            <span className="label">Status</span>
            <select className="input mt-1" value={status}
                    onChange={(e) => setFilter('status', e.target.value)}>
              <option value="">Any</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>
            <span className="label">Merchant</span>
            <select className="input mt-1" value={merchantId}
                    onChange={(e) => setFilter('merchantId', e.target.value)}>
              <option value="">Any</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code}{m.accountType && m.accountType !== 'GENERAL' ? ` (${m.accountType})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="label">Agent reference</span>
            <input className="input mt-1" value={agentReference} placeholder="HRE-CBD01-…"
                   onChange={(e) => setFilter('agentReference', e.target.value)} />
          </label>
          <label>
            <span className="label">Target</span>
            <input className="input mt-1" value={target} placeholder="0772929223"
                   onChange={(e) => setFilter('target', e.target.value)} />
          </label>
          <label>
            <span className="label">Min amount</span>
            <input className="input mt-1 tabular" value={minAmount} inputMode="decimal"
                   onChange={(e) => setFilter('minAmount', e.target.value)} />
          </label>
          <label>
            <span className="label">Max amount</span>
            <input className="input mt-1 tabular" value={maxAmount} inputMode="decimal"
                   onChange={(e) => setFilter('maxAmount', e.target.value)} />
          </label>
          <label>
            <span className="label">From</span>
            <input className="input mt-1" type="date" value={from.slice(0, 10)}
                   onChange={(e) => setFilter('from',
                     e.target.value ? `${e.target.value}T00:00:00Z` : '')} />
          </label>
        </div>
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          Target search matches the full stored number, even where the column below is masked for
          your role.
        </p>
      </Card>

      <Card
        title={
          <span>
            Orders
            {data && (
              <span className="ml-2 font-normal text-ink-500">
                {data.totalElements.toLocaleString()} match
                {isFetching && <span className="ml-2 text-ink-400">refreshing…</span>}
              </span>
            )}
          </span>
        }
      >
        {isLoading && <Spinner />}
        {error && <ErrorNote error={error} />}
        {data && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-ink-200">
                  <tr>
                    <th className="th">Reference</th>
                    <th className="th">Merchant</th>
                    <th className="th">Status</th>
                    <th className="th">Product</th>
                    <th className="th">Target</th>
                    <th className="th text-right">Amount</th>
                    <th className="th">Message</th>
                    <th className="th">Placed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {data.content.map((order) => (
                    <tr key={order.id} className="hover:bg-ink-50">
                      <td className="td">
                        <Link to={`/orders/${order.id}`} className="hover:underline">
                          <Reference value={order.agentReference} />
                        </Link>
                      </td>
                      <td className="td text-ink-600">{order.merchantCode}</td>
                      <td className="td"><StatusPill status={order.status} /></td>
                      <td className="td tabular text-ink-600">
                        {order.productId}
                        {order.productCode && (
                          <span className="ml-1 mono text-ink-500">{order.productCode}</span>
                        )}
                      </td>
                      <td className="td mono">{order.target}</td>
                      <td className="td text-right">
                        <Money amount={money(order.amount)} currency={order.currency} />
                      </td>
                      <td className="td max-w-xs truncate text-ink-600" title={order.message ?? ''}>
                        {order.message ?? '—'}
                      </td>
                      <td className="td"><Timestamp value={order.createdAt} /></td>
                    </tr>
                  ))}
                  {data.content.length === 0 && (
                    <tr><td className="td py-8 text-center text-ink-500" colSpan={8}>
                      Nothing matches these filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-ink-200 px-4 py-2.5">
              <span className="text-xs text-ink-500">
                Page {data.page + 1} of {Math.max(1, data.totalPages)}
              </span>
              <div className="flex gap-2">
                <button type="button" className="btn-secondary" disabled={page === 0}
                        onClick={() => setPage((p) => p - 1)}>Previous</button>
                <button type="button" className="btn-secondary"
                        disabled={data.page + 1 >= data.totalPages}
                        onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
